import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    UserIcon,
    MagnifyingGlassIcon,
    EnvelopeIcon,
    PencilSquareIcon,
    CheckIcon,
    GlobeAltIcon,
    ArrowRightOnRectangleIcon,
    LockClosedIcon,
    StarIcon,
    ChartBarIcon,
    CurrencyPoundIcon,
    WrenchScrewdriverIcon,
    BuildingStorefrontIcon,
    QuestionMarkCircleIcon
} from '@heroicons/react/24/outline';
import Helper from '../helpers';
import config from '../config';
import ContactSupportModal from '../components/ContactSupportModal';

const baseUrl = config.apiBaseUrl;

// Base URLs for each social platform — stored value is username only
const SOCIAL_BASES = {
    tikTokUrl:    'tiktok.com/@',
    blueskyUrl:   'bsky.app/profile/',
    instagramUrl: 'instagram.com/',
    facebookUrl:  'facebook.com/',
    stravaUrl:    'strava.com/athletes/',
    linkedInUrl:  'linkedin.com/in/',
};

const stripBase = (key, value) => {
    if (!value) return '';
    const base = SOCIAL_BASES[key];
    if (!base) return value;
    let v = value.replace(/^https?:\/\/(www\.)?/, '').replace(/^www\./, '');
    if (v.toLowerCase().startsWith(base.toLowerCase())) v = v.slice(base.length);
    if (!base.endsWith('@') && v.startsWith('@')) v = v.slice(1);
    return v.replace(/\/$/, '');
};

const SERVICE_OPTIONS = [
    { key: 'diet_meal_plans', label: 'Diet / Meal Plans' },
    { key: 'sports_massage', label: 'Sports Massage' },
    { key: 'consultations', label: 'Consultations' },
    { key: 'online_coaching', label: 'Online Coaching' },
    { key: 'group_sessions', label: 'Group Sessions' },
    { key: 'nutrition_advice', label: 'Nutrition Advice' },
    { key: 'body_composition', label: 'Body Composition Testing' },
    { key: 'rehabilitation', label: 'Rehabilitation / Injury Support' },
    { key: 'pre_post_natal', label: 'Pre / Post Natal' },
    { key: 'mindset_coaching', label: 'Mindset & Wellbeing Coaching' },
    { key: 'strength_conditioning', label: 'Strength & Conditioning' },
    { key: 'youth_training', label: 'Youth / Junior Training' },
];

const PtDirectoryDashboard = () => {
    const navigate = useNavigate();
    const [listing, setListing] = useState(null);
    const [enquiries, setEnquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saveMsg, setSaveMsg] = useState('');
    const [activeTab, setActiveTab] = useState('listing');
    const [supportModalOpen, setSupportModalOpen] = useState(false);

    // Edit form state
    const [editForm, setEditForm] = useState({});
    const [selectedServices, setSelectedServices] = useState([]);

    const token = () => Helper.getCookie('wh-jwt');

    const fetchMyListing = useCallback(async () => {
        try {
            const res = await fetch(`${baseUrl}/api/ptdirectory/my-listing`, {
                headers: { Authorization: `Bearer ${token()}` }
            });
            if (res.ok) {
                const data = await res.json();
                if (data.listing) {
                    setListing(data.listing);
                    setEditForm({
                        name: data.listing.name || '',
                        city: data.listing.city || '',
                        postcode: data.listing.postcode || '',
                        bio: data.listing.bio || '',
                        specialisms: data.listing.specialisms || '',
                        priceType: data.listing.priceType || 'from',
                        priceFrom: data.listing.priceFrom || '',
                        priceTo: data.listing.priceTo || '',
                        beaconsUsername: data.listing.beaconsUsername || '',
                        tikTokUrl:    stripBase('tikTokUrl',    data.listing.tikTokUrl),
                        blueskyUrl:   stripBase('blueskyUrl',   data.listing.blueskyUrl),
                        instagramUrl: stripBase('instagramUrl', data.listing.instagramUrl),
                        facebookUrl:  stripBase('facebookUrl',  data.listing.facebookUrl),
                        stravaUrl:    stripBase('stravaUrl',    data.listing.stravaUrl),
                        linkedInUrl:  stripBase('linkedInUrl',  data.listing.linkedInUrl),
                    });
                    setSelectedServices(
                        data.listing.services
                            ? data.listing.services.split(',').map(s => s.trim()).filter(Boolean)
                            : []
                    );
                }
            }
        } catch (err) {
            console.error('Error fetching listing', err);
        }
    }, []);

    const fetchEnquiries = useCallback(async () => {
        try {
            const res = await fetch(`${baseUrl}/api/ptdirectory/my-enquiries`, {
                headers: { Authorization: `Bearer ${token()}` }
            });
            if (res.ok) {
                const data = await res.json();
                setEnquiries(data.enquiries || []);
            }
        } catch (err) {
            console.error('Error fetching enquiries', err);
        }
    }, []);

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            await Promise.all([fetchMyListing(), fetchEnquiries()]);
            setLoading(false);
        };
        init();
    }, [fetchMyListing, fetchEnquiries]);

    const handleSave = async () => {
        setSaving(true);
        setSaveMsg('');
        try {
            const payload = {
                ...editForm,
                priceFrom: editForm.priceFrom ? parseFloat(editForm.priceFrom) : null,
                priceTo: editForm.priceFrom && editForm.priceType === 'range' ? parseFloat(editForm.priceTo) : null,
                services: selectedServices.join(','),
                // Store usernames only
                tikTokUrl:    stripBase('tikTokUrl',    editForm.tikTokUrl),
                blueskyUrl:   stripBase('blueskyUrl',   editForm.blueskyUrl),
                instagramUrl: stripBase('instagramUrl', editForm.instagramUrl),
                facebookUrl:  stripBase('facebookUrl',  editForm.facebookUrl),
                stravaUrl:    stripBase('stravaUrl',    editForm.stravaUrl),
                linkedInUrl:  stripBase('linkedInUrl',  editForm.linkedInUrl),
            };
            const res = await fetch(`${baseUrl}/api/ptdirectory/my-listing`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token()}`
                },
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                const data = await res.json();
                setListing(prev => ({ ...prev, ...data.listing }));
                setSaveMsg('Saved successfully!');
                setTimeout(() => setSaveMsg(''), 3000);
            } else {
                setSaveMsg('Failed to save. Please try again.');
            }
        } catch {
            setSaveMsg('Failed to save. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const toggleService = (key) => {
        setSelectedServices(prev =>
            prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]
        );
    };

    const handleSignOut = async () => {
        await fetch(`${baseUrl}/api/users/signout`, { method: 'POST', credentials: 'include' });
        document.cookie = 'wh-jwt=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        navigate('/login');
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-luxury-black flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
            </div>
        );
    }

    const tabs = [
        { id: 'listing', label: 'My Listing', icon: UserIcon },
        { id: 'enquiries', label: `Enquiries ${enquiries.length > 0 ? `(${enquiries.length})` : ''}`, icon: EnvelopeIcon },
        { id: 'social', label: 'Social & Links', icon: GlobeAltIcon },
    ];

    return (
        <div className="min-h-screen bg-luxury-black">
            {/* Nav */}
            <nav className="border-b border-luxury-graphite/30 sticky top-0 z-10 bg-luxury-black/95 backdrop-blur">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-amber-500/10 rounded-lg flex items-center justify-center">
                                <MagnifyingGlassIcon className="h-5 w-5 text-amber-400" />
                            </div>
                            <div>
                                <h1 className="text-lg font-display font-bold text-gradient-emerald leading-tight">
                                    PT Directory
                                </h1>
                                {listing?.isFounderMember && (
                                    <span className="text-xs text-amber-400 flex items-center gap-1">
                                        <StarIcon className="h-3 w-3" /> Foundation Member
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {listing?.slug && (
                                <a
                                    href={`/find-pt/${listing.slug}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-emerald-400 hover:text-emerald-300 border border-emerald-600/30 rounded-lg px-3 py-1.5 transition-colors"
                                >
                                    View Public Profile
                                </a>
                            )}
                            <button
                                onClick={() => setSupportModalOpen(true)}
                                className="flex items-center gap-1.5 text-luxury-pearl/50 hover:text-luxury-pearl text-sm transition-colors"
                                title="Contact Support"
                            >
                                <QuestionMarkCircleIcon className="h-4 w-4" />
                                <span className="hidden sm:inline">Contact Us</span>
                            </button>
                            <button
                                onClick={handleSignOut}
                                className="flex items-center gap-1.5 text-luxury-pearl/50 hover:text-luxury-pearl text-sm transition-colors"
                            >
                                <ArrowRightOnRectangleIcon className="h-4 w-4" />
                                Sign Out
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Account type banner */}
                <div className="card-luxury p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${listing?.isFounderMember ? 'bg-amber-500/10' : 'bg-emerald-600/10'}`}>
                            {listing?.isFounderMember
                                ? <StarIcon className="h-5 w-5 text-amber-400" />
                                : <MagnifyingGlassIcon className="h-5 w-5 text-emerald-400" />
                            }
                        </div>
                        <div>
                            <p className="text-luxury-pearl font-semibold text-sm">
                                {listing?.isFounderMember ? 'PT Directory – Foundation Member' : 'PT Directory'}
                            </p>
                            {listing?.isFounderMember && (
                                <p className="text-amber-400/80 text-xs flex items-center gap-1">
                                    <LockClosedIcon className="h-3 w-3" /> Lifetime pricing locked in
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => navigate('/plans')}
                            className="flex items-center gap-2 text-xs border border-emerald-600/30 hover:border-emerald-600/60 bg-emerald-600/5 hover:bg-emerald-600/10 text-emerald-300 rounded-lg px-3 py-2 transition-all"
                        >
                            <BuildingStorefrontIcon className="h-4 w-4" />
                            Upgrade to Venue Plan
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 mb-6 bg-luxury-graphite/20 rounded-xl p-1">
                    {tabs.map(tab => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-sm font-semibold transition-all ${
                                    activeTab === tab.id
                                        ? 'bg-emerald-600 text-luxury-black shadow'
                                        : 'text-luxury-pearl/50 hover:text-luxury-pearl'
                                }`}
                            >
                                <Icon className="h-4 w-4" />
                                <span className="hidden sm:inline">{tab.label}</span>
                            </button>
                        );
                    })}
                </div>

                {/* ── Listing Tab ── */}
                {activeTab === 'listing' && (
                    <div className="space-y-6">
                        <div className="card-luxury p-6">
                            <div className="flex items-center gap-2 mb-5">
                                <PencilSquareIcon className="h-5 w-5 text-emerald-300" />
                                <h2 className="text-lg font-semibold text-luxury-pearl">Your Directory Profile</h2>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-luxury-pearl/60 mb-1">Display Name *</label>
                                    <input
                                        value={editForm.name || ''}
                                        onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                                        className="w-full px-3 py-2 rounded-lg bg-luxury-graphite/30 border border-emerald-600/20 text-luxury-pearl placeholder-luxury-pearl/30 focus:ring-2 focus:ring-emerald-600 focus:outline-none text-sm"
                                        placeholder="Your name"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-luxury-pearl/60 mb-1">City *</label>
                                    <input
                                        value={editForm.city || ''}
                                        onChange={e => setEditForm(p => ({ ...p, city: e.target.value }))}
                                        className="w-full px-3 py-2 rounded-lg bg-luxury-graphite/30 border border-emerald-600/20 text-luxury-pearl placeholder-luxury-pearl/30 focus:ring-2 focus:ring-emerald-600 focus:outline-none text-sm"
                                        placeholder="Your city"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-luxury-pearl/60 mb-1">Postcode</label>
                                    <input
                                        value={editForm.postcode || ''}
                                        onChange={e => setEditForm(p => ({ ...p, postcode: e.target.value }))}
                                        className="w-full px-3 py-2 rounded-lg bg-luxury-graphite/30 border border-emerald-600/20 text-luxury-pearl placeholder-luxury-pearl/30 focus:ring-2 focus:ring-emerald-600 focus:outline-none text-sm"
                                        placeholder="e.g. SW1A 1AA"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-luxury-pearl/60 mb-1">Specialisms</label>
                                    <input
                                        value={editForm.specialisms || ''}
                                        onChange={e => setEditForm(p => ({ ...p, specialisms: e.target.value }))}
                                        className="w-full px-3 py-2 rounded-lg bg-luxury-graphite/30 border border-emerald-600/20 text-luxury-pearl placeholder-luxury-pearl/30 focus:ring-2 focus:ring-emerald-600 focus:outline-none text-sm"
                                        placeholder="e.g. Weight Loss, Strength, HIIT"
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="block text-xs font-medium text-luxury-pearl/60 mb-1">Bio</label>
                                    <textarea
                                        value={editForm.bio || ''}
                                        onChange={e => setEditForm(p => ({ ...p, bio: e.target.value }))}
                                        rows={4}
                                        className="w-full px-3 py-2 rounded-lg bg-luxury-graphite/30 border border-emerald-600/20 text-luxury-pearl placeholder-luxury-pearl/30 focus:ring-2 focus:ring-emerald-600 focus:outline-none text-sm resize-none"
                                        placeholder="Tell potential clients about yourself..."
                                    />
                                </div>
                            </div>

                            {/* Pricing */}
                            <div className="mt-5">
                                <label className="block text-xs font-medium text-luxury-pearl/60 mb-2 flex items-center gap-1">
                                    <CurrencyPoundIcon className="h-3.5 w-3.5" /> Session Pricing
                                </label>
                                <div className="flex gap-3 flex-wrap">
                                    <select
                                        value={editForm.priceType || 'from'}
                                        onChange={e => setEditForm(p => ({ ...p, priceType: e.target.value }))}
                                        className="px-3 py-2 rounded-lg bg-luxury-graphite/30 border border-emerald-600/20 text-luxury-pearl focus:ring-2 focus:ring-emerald-600 focus:outline-none text-sm"
                                    >
                                        <option value="from">From price</option>
                                        <option value="range">Price range</option>
                                    </select>
                                    <input
                                        type="number"
                                        value={editForm.priceFrom || ''}
                                        onChange={e => setEditForm(p => ({ ...p, priceFrom: e.target.value }))}
                                        className="w-28 px-3 py-2 rounded-lg bg-luxury-graphite/30 border border-emerald-600/20 text-luxury-pearl placeholder-luxury-pearl/30 focus:ring-2 focus:ring-emerald-600 focus:outline-none text-sm"
                                        placeholder="£ from"
                                    />
                                    {editForm.priceType === 'range' && (
                                        <input
                                            type="number"
                                            value={editForm.priceTo || ''}
                                            onChange={e => setEditForm(p => ({ ...p, priceTo: e.target.value }))}
                                            className="w-28 px-3 py-2 rounded-lg bg-luxury-graphite/30 border border-emerald-600/20 text-luxury-pearl placeholder-luxury-pearl/30 focus:ring-2 focus:ring-emerald-600 focus:outline-none text-sm"
                                            placeholder="£ to"
                                        />
                                    )}
                                </div>
                            </div>

                            {/* Services */}
                            <div className="mt-5">
                                <label className="block text-xs font-medium text-luxury-pearl/60 mb-2 flex items-center gap-1">
                                    <WrenchScrewdriverIcon className="h-3.5 w-3.5" /> Services Offered
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {SERVICE_OPTIONS.map(({ key, label }) => (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => toggleService(key)}
                                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                                                selectedServices.includes(key)
                                                    ? 'border-emerald-500 bg-emerald-900/30 text-emerald-300'
                                                    : 'border-luxury-graphite/50 text-luxury-pearl/50 hover:border-luxury-pearl/30'
                                            }`}
                                        >
                                            {selectedServices.includes(key) && <CheckIcon className="h-3 w-3 inline mr-1" />}
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Save */}
                            <div className="mt-6 flex items-center gap-3">
                                <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="btn-primary flex items-center gap-2 px-6 py-2.5"
                                >
                                    {saving ? (
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-luxury-black"></div>
                                    ) : (
                                        <CheckIcon className="h-4 w-4" />
                                    )}
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </button>
                                {saveMsg && (
                                    <span className={`text-sm ${saveMsg.includes('success') ? 'text-emerald-400' : 'text-red-400'}`}>
                                        {saveMsg}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Enquiries Tab ── */}
                {activeTab === 'enquiries' && (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <ChartBarIcon className="h-5 w-5 text-emerald-300" />
                                <h2 className="text-lg font-semibold text-luxury-pearl">Client Enquiries</h2>
                            </div>
                            <span className="text-luxury-pearl/50 text-sm">{enquiries.length} total</span>
                        </div>

                        {enquiries.length === 0 ? (
                            <div className="card-luxury p-8 text-center">
                                <EnvelopeIcon className="h-12 w-12 text-luxury-pearl/20 mx-auto mb-3" />
                                <p className="text-luxury-pearl/50">No enquiries yet.</p>
                                <p className="text-luxury-pearl/30 text-sm mt-1">When potential clients contact you through the directory, they'll appear here.</p>
                            </div>
                        ) : (
                            enquiries.map(enq => (
                                <div key={enq.id} className="card-luxury p-5">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex-1">
                                            <p className="text-luxury-pearl font-medium text-sm">
                                                {enq.enquirerName || 'Anonymous'}
                                            </p>
                                            <p className="text-emerald-400 text-xs mt-0.5">{enq.enquirerEmail}</p>
                                            {enq.message && (
                                                <p className="text-luxury-pearl/60 text-sm mt-2 leading-relaxed">{enq.message}</p>
                                            )}
                                        </div>
                                        <p className="text-luxury-pearl/30 text-xs whitespace-nowrap">
                                            {new Date(enq.createdAt).toLocaleDateString('en-GB', {
                                                day: 'numeric', month: 'short', year: 'numeric'
                                            })}
                                        </p>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* ── Social & Links Tab ── */}
                {activeTab === 'social' && (
                    <div className="card-luxury p-6 space-y-4">
                        <div className="flex items-center gap-2 mb-2">
                            <GlobeAltIcon className="h-5 w-5 text-emerald-300" />
                            <h2 className="text-lg font-semibold text-luxury-pearl">Social Profiles & Links</h2>
                        </div>
                        <p className="text-luxury-pearl/50 text-sm">These links appear on your public directory profile to help clients connect with you.</p>

                        {[
                            { key: 'beaconsUsername', label: 'Beacons',   placeholder: 'yourname', prefix: 'beacons.ai/' },
                            { key: 'instagramUrl',    label: 'Instagram', placeholder: 'yourprofile' },
                            { key: 'tikTokUrl',       label: 'TikTok',    placeholder: 'yourprofile' },
                            { key: 'facebookUrl',     label: 'Facebook',  placeholder: 'yourpage' },
                            { key: 'stravaUrl',       label: 'Strava',    placeholder: '12345678' },
                            { key: 'blueskyUrl',      label: 'Bluesky',   placeholder: 'yourhandle.bsky.social' },
                            { key: 'linkedInUrl',     label: 'LinkedIn',  placeholder: 'yourprofile' },
                        ].map(({ key, label, placeholder, prefix }) => (
                            <div key={key}>
                                <label className="block text-xs font-medium text-luxury-pearl/60 mb-1">{label}</label>
                                <div className="flex items-center">
                                    <span className="text-xs text-luxury-pearl/40 bg-luxury-graphite/30 border border-r-0 border-emerald-600/20 rounded-l-lg px-3 py-2 h-9 flex items-center whitespace-nowrap">
                                        {prefix || SOCIAL_BASES[key]}
                                    </span>
                                    <input
                                        value={editForm[key] || ''}
                                        onChange={e => setEditForm(p => ({ ...p, [key]: stripBase(key, e.target.value) }))}
                                        className="flex-1 px-3 py-2 bg-luxury-graphite/30 border border-emerald-600/20 text-luxury-pearl placeholder-luxury-pearl/30 focus:ring-2 focus:ring-emerald-600 focus:outline-none text-sm h-9 rounded-r-lg"
                                        placeholder={placeholder}
                                    />
                                </div>
                            </div>
                        ))}

                        <div className="flex items-center gap-3 pt-2">
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="btn-primary flex items-center gap-2 px-6 py-2.5"
                            >
                                {saving ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-luxury-black"></div>
                                ) : (
                                    <CheckIcon className="h-4 w-4" />
                                )}
                                {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                            {saveMsg && (
                                <span className={`text-sm ${saveMsg.includes('success') ? 'text-emerald-400' : 'text-red-400'}`}>
                                    {saveMsg}
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <ContactSupportModal
                isOpen={supportModalOpen}
                onClose={() => setSupportModalOpen(false)}
                userType="PT Director"
            />
        </div>
    );
};

export default PtDirectoryDashboard;
